import { isTrackEventLoggingCookieSet } from "@/react/utils/isCookieSet";

const EVENT_NAMES = {
  pageView: "PageView",
  pageExit: "PageExit",
  videoView: "VideoView",
  videoSegmentView: "VideoSegmentView",
  postView: "PostView",
};

let url: string | undefined;
let token: string | undefined;

const PLATFORM = "web";
const APP_NAME = `circle.${window.RAILS_ENV}`;
type PropertyValue =
  | string
  | number
  | boolean
  | null
  | Date
  | undefined
  | object;
export type PropertiesType = { community_id?: number } & Record<
  string,
  PropertyValue
>;

interface IframeDetection {
  isInIframe: boolean;
}

export const detectIframe = (): IframeDetection => {
  try {
    return {
      isInIframe: window.self !== window.top,
    };
  } catch {
    // Expected: Cross-origin iframe access throws security exception
    return {
      isInIframe: true,
    };
  }
};

// Cache iframe detection result at module level since it won't change during page lifecycle
const iframeDetection = detectIframe();

const trackEvent = async (eventName: string, properties: PropertiesType) => {
  const { isInIframe } = iframeDetection;

  if (!url) {
    url = window && window.ANALYTICS_TRACKER_URL;
  }

  if (!token) {
    token = window && window.ANALYTICS_TRACKER_API_TOKEN;
  }

  const tenantId = (properties.community_id ?? 0).toString();

  const defaultProperties = {
    platform: PLATFORM,
    ahoy_visitor: window.ANALYTICS_TRACKER_VISITOR,
    is_in_iframe: isInIframe,
  };

  const data = {
    event_name: eventName,
    tenant_id: tenantId,
    app_name: APP_NAME,
    triggered_at: new Date().toISOString(),
    properties: { ...defaultProperties, ...properties },
  };

  let body;
  try {
    body = JSON.stringify(data);
  } catch (error) {
    console.error("Error while converting data to JSON", error);
    return;
  }

  if (
    isTrackEventLoggingCookieSet() ||
    window.ANALYTICS_TRACKER_LOG_IN_CONSOLE === "true"
  ) {
    // eslint-disable-next-line no-console -- Log info to console. Needed for testing
    console.info("useAnalyticsTracker.trackEvent", data);
  }

  if (url && token) {
    // Try sendBeacon first if available
    if (navigator.sendBeacon) {
      const urlObject = new URL(url);
      urlObject.searchParams.set("token", token);
      navigator.sendBeacon(urlObject.toString(), body);
      return;
    }
    // Fall back to fetch if sendBeacon is unavailable or fails
    const headers = {
      "Content-Type": "application/json",
      Authorization: `${token}`,
    };

    await fetch(url, {
      method: "POST",
      headers,
      body,
      keepalive: true,
    });
  }
};

const trackPageView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.pageView, properties);

const trackPageExit = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.pageExit, properties);

const trackVideoView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.videoView, properties);

const trackVideoSegmentView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.videoSegmentView, properties);

const trackPostView = (properties: PropertiesType) =>
  trackEvent(EVENT_NAMES.postView, properties);

const trackEventsForRevops = (eventName: string, properties: PropertiesType) =>
  trackEvent(eventName, properties);

export const analyticsTrackerApi = {
  trackPageView,
  trackPageExit,
  trackVideoView,
  trackVideoSegmentView,
  trackPostView,
  trackEvent,
  trackEventsForRevops,
};
